<template>
    <div
        @focusin="onFocus"
        @focusout="onFocusout"
    >
        <show-all-new
            :title="pageTitle"
            :items="items"
            :line-id="lineId"
            :is-loading="isLoading"
            @item-selected="itemSelected"
            @request-more="fetchMoreItems"
        />
    </div>
</template>

<script lang="ts" setup>
    import { type ComputedRef, computed } from "vue";
    import { linkItems, ShowAllContentType } from "@/components/Header/types/LinkItemsCollection";
    import { useRouter } from 'vue-router';
    import { useSearchPageStore } from "@/stores/SearchPage";
    import { storeToRefs } from 'pinia';
    import type { IEventSelected } from "@/components/MultiLineView/types";
    import { usePlayerStore } from "@/stores/Player";
    import { useApplicationStore } from "@/stores/Application";
    import type { ChannelLiveCardData, ProgramCardData, OnDemandCardData, CreditCardData } from "@/components/Cards/types";
    import { useUserInfoStore } from "@/stores/UserInfo";
    import { Logger } from "@/utils";
    import { useDetailPageStore } from "@/stores/DetailPage";
    import { AppConstants } from "@/common";
    import { Strings } from '@/locales/types/enums';
    import { useCastPageStore } from "@/stores/CastPage";
   
    const {
        setViewedCastDetailItems
    } = useCastPageStore();

    const { 
        setPlayBtnUnavailable,
        setViewedDetailItems
     } = useDetailPageStore();

    const PlayerStoreModule = usePlayerStore();
    const {
        startPlayingChannel
    } = PlayerStoreModule;

    const ApplicationStore = useApplicationStore();
    const {
        showPreviewSignInModal,
        setTooltipeShowing,
        setTooltipeMessage
    } = ApplicationStore;

    const UserInfoStoreModule = useUserInfoStore();
    const {
        isGuest,
    } = storeToRefs(UserInfoStoreModule);

    const SearchPageStore = useSearchPageStore();
    const {
        loadShowAllNextPage,
    } = SearchPageStore;
    const {
        getShowAllCards: items,
        getIsLoading: isLoading,
        getTypesPage,
        getChannelsTitle,
        getLiveTitle,
        getMoviesTitle,
        getShowsTitle,
        getCreditsTitle,
    } = storeToRefs(SearchPageStore);

    definePageMeta({ 
        layout: 'default-no-header',
        middleware: ['set-page-indicator', 'fetch-show-all-search'],
    });

    const { t } = useI18n();
    const { $navigator } = useNuxtApp();
    const router = useRouter();
    const { shelfKeys }: typeof AppConstants = AppConstants;

    const lineId: ComputedRef<string> = computed(() => {
        let _id: string;
        switch (getTypesPage.value) {
            case ShowAllContentType.Channels:
                _id = shelfKeys.searchResultChannelsCards;
                break;
            case ShowAllContentType.Live:
                _id = shelfKeys.searchResultLiveCards;
                break;
            case ShowAllContentType.Movies:
                _id = shelfKeys.searchResultMoviesCards;
                break;
            case ShowAllContentType.Shows:
                _id = shelfKeys.searchResultShowsCards;
                break;
            case ShowAllContentType.Credits:
                _id = shelfKeys.searchResultCreditsCards;
                break;
            default:
                throw new Error(`ShowAll: Unknown type page ${getTypesPage.value}`);
        }
        
        return `${_id}-${shelfKeys.showAll}`;
    });
    const pageTitle: ComputedRef<string> = computed(() => {
        switch (getTypesPage.value) {
            case ShowAllContentType.Channels:
                return getChannelsTitle.value;
            case ShowAllContentType.Live:
                return getLiveTitle.value;
            case ShowAllContentType.Movies:
                return getMoviesTitle.value;
            case ShowAllContentType.Shows:
                return getShowsTitle.value;
            case ShowAllContentType.Credits:
                return getCreditsTitle.value;
            default:
                Logger.captureError(`ShowAll: Unknown type page ${getTypesPage.value}` as unknown as Error);
                return "";
        }
    });

    const fetchMoreItems = async (): Promise<void> => {
        await loadShowAllNextPage();
    };
    const itemSelected = (val: IEventSelected): void => {
        switch (getTypesPage.value) {
            case ShowAllContentType.Channels:
                onPlayChannel(val.item as ChannelLiveCardData);
                break;
        
            case ShowAllContentType.Live:
                onLiveSelectedSearchShowAll(val.item as ProgramCardData);
                break;

            case ShowAllContentType.Movies:
                onMoviesSelectedSearchShowAll(val.item as OnDemandCardData);
                break;

            case ShowAllContentType.Shows:
                onShowsSelectedSearchShowAll(val.item as OnDemandCardData);
                break;

            default:
                onCastSelectedSearchShowAllPage(val.item as CreditCardData);
                break;
        }
    };
    const onCastSelectedSearchShowAllPage = (credit: CreditCardData) => {
        try {
            const creditItem = credit.getCreditItem();
            if (!creditItem) {
                throw new Error("СreditItem is null:" + JSON.stringify(credit));
            }

            const personId = credit.getPersonId();
            if (!personId) {
                throw new Error("The person ID is null:" + JSON.stringify(credit));
            }

            setViewedCastDetailItems(creditItem);

            void router.push(`${linkItems.detailCast.routerPath}/${personId}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onPlayChannel = (item: ChannelLiveCardData) =>{
        if (isGuest.value) {
            setTimeout(() => {
                showPreviewSignInModal(true);
            }, 150);
            return;
        }
        void startPlayingChannel(item);
    };

    const onShowsSelectedSearchShowAll = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            void router.push(`${linkItems.detailsVodSeries.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onMoviesSelectedSearchShowAll = (item: OnDemandCardData): void => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);
            void router.push(`${linkItems.detailsVodMovies.routerPath}/${item.getId()}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onLiveSelectedSearchShowAll = (item: ProgramCardData) => {
        try {
            const playItem = item.getPlayItem();
            if (!playItem) {
                throw new Error("playItem is null:" + JSON.stringify(item));
            }
            setViewedDetailItems(playItem);

            // Disable play btn
            setPlayBtnUnavailable(true);
            void router.push(`${linkItems.detailsPrograms.routerPath}/${playItem?.id ? playItem?.id : ''}`);
        } catch (error) {
            Logger.captureError(error as Error);
            setTooltipeMessage(t(Strings.DETAILS_TOOLTIP_ERROR_OPENING_DETAILS_PAGE).toString());
            setTooltipeShowing({visible: true});
        }
    };
    const onFocus = () => {
        registerKeyListeners();
    };
    const onFocusout = () => {
        unregisterKeyListeners();
    };
    const keyBackListener = (): void => {
        router.go(-1);
    };
    const registerKeyListeners = (): void => {
        $navigator.on("keypress:back", keyBackListener);
    };
    const unregisterKeyListeners = (): void => {
        $navigator.off("keypress:back", keyBackListener);
    };
</script>
